import React, { useMemo, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import CalendarBox from "../components/CalendarBox";
import { motion } from "framer-motion";
import { UserContext } from "../App";
import { CalendarIcon } from '@heroicons/react/24/outline';
import { getTradingPerformance } from '../statsUtils';
import { isTapeReading } from '../utils/tradeHelpers';
import YearSelector from '../components/YearSelector';
import {
  DEFAULT_CALENDAR_CARD_APPEARANCE,
  sanitizeCalendarCardAppearance,
} from '../utils/calendarCardAppearance';

const months = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];
const DEFAULT_BE_THRESHOLD = 20;

function sanitizeBeThreshold(value) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return DEFAULT_BE_THRESHOLD;
  if (parsed < 1) return 1;
  if (parsed > 1000) return 1000;
  return Math.round(parsed);
}

const HomePage = () => {
  const { currentUser, selectedAccount, dataRefreshTrigger } = useContext(UserContext);
  const navigate = useNavigate();
  // Year dropdown logic: dynamic range based on current year
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [beThreshold, setBeThreshold] = useState(DEFAULT_BE_THRESHOLD);
  const [calendarCardAppearance, setCalendarCardAppearance] = useState(DEFAULT_CALENDAR_CARD_APPEARANCE);

  useEffect(() => {
    if (!currentUser || !selectedAccount) {
      setLoading(false);
      return;
    }
    const fetchEntries = async () => {
      setLoading(true);
      try {
        const { db } = await import('../firebase');
        const { collection, getDocs } = await import('firebase/firestore');
        const entriesCol = collection(db, 'users', currentUser.uid, 'accounts', selectedAccount.id, 'entries');
        const snap = await getDocs(entriesCol);
        const data = snap.docs.map(doc => doc.data());
        
        
        setEntries(data);
      } catch (error) {
        console.error('Error fetching entries:', error);
        setEntries([]);
      } finally {
        setLoading(false);
      }
    };
    fetchEntries();
  }, [currentUser, selectedAccount, dataRefreshTrigger]);


  useEffect(() => {
    if (!currentUser?.uid) return;

    const loadSettings = async () => {
      try {
        const { db } = await import('../firebase');
        const { doc, getDoc } = await import('firebase/firestore');
        const prefsRef = doc(db, 'users', currentUser.uid, 'preferences', 'settings');
        const prefsSnap = await getDoc(prefsRef);
        if (prefsSnap.exists()) {
          const data = prefsSnap.data();
          setBeThreshold(sanitizeBeThreshold(data.beThreshold));
          setCalendarCardAppearance(sanitizeCalendarCardAppearance(data.calendarCardAppearance));
        }
      } catch (error) {
        console.error('Error loading HomePage settings:', error);
      }
    };

    loadSettings();
  }, [currentUser]);

  // Aggregate P&L, trade counts, and calculate percentages by month
  // Now uses the same calculation method as CalendarPage and SummaryPage via getTradingPerformance
  const monthData = useMemo(() => {
    const data = Array(12).fill(null).map(() => ({ pnl: 0, count: 0, percentage: 0 }));
    
    // Process entries for the selected year and calculate monthly data
    entries.forEach(entry => {
      if (String(entry.year) === String(year)) {
        const idx = parseInt(entry.month, 10) - 1;
        if (idx >= 0 && idx < 12) {
          // Only count actual trades (not deposits, payouts, tape reading, or reset-excluded)
          const isActualTrade = !entry.isDeposit && !entry.isPayout && !isTapeReading(entry) && !entry.isResetExcluded && entry.pnl !== undefined && entry.pnl !== null && entry.pnl !== "";
          if (isActualTrade) {
            // Use precision-safe addition to avoid floating point errors
            const currentPnl = data[idx].pnl;
            const newPnl = Number(entry.pnl) || 0;
            data[idx].pnl = Math.round((currentPnl + newPnl) * 100) / 100;
            data[idx].count += 1;
          }
        }
      }
    });
    
    // Calculate percentages for each month using the same logic as other pages
    data.forEach((month, idx) => {
      if (month.count > 0) {
        // Use getTradingPerformance to get the percentage for this month
        const monthPerformance = getTradingPerformance(entries, year, idx + 1);
        if (monthPerformance) {
          month.percentage = monthPerformance.percentage;
        }
      }
    });
    
    return data;
  }, [entries, year]);

  return (
    <div className="page-shell animate-fadeIn">
      {loading ? (
        <div className="flex flex-col gap-6 w-full max-w-5xl mx-auto py-24">
          <div className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card h-16 w-1/3 animate-pulse mb-4 shadow-subtle" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 sm:gap-6 lg:gap-10">
            {[...Array(12)].map((_,i) => <div key={i} className="bg-white dark:bg-[#1F2937] border border-[#E5E7EB] dark:border-[#374151] rounded-card h-48 animate-pulse shadow-subtle" />)}
          </div>
        </div>
      ) : !selectedAccount ? (
        <div className="flex flex-col items-center justify-center min-h-screen">
          <div className="text-center">
            <h2 className="text-2xl font-semibold text-[#111827] dark:text-[#F9FAFB] mb-4">No Account Selected</h2>
            <p className="text-[#6B7280] dark:text-[#9CA3AF] mb-6">Please wait while we set up your account...</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#3B82F6] dark:border-[#60A5FA] mx-auto"></div>
          </div>
        </div>
      ) : (
        <>
          <div className="flex min-w-0 items-center gap-2.5 sm:gap-3 mb-8 mt-2">
            <CalendarIcon className="w-6 h-6 sm:w-7 sm:h-7 text-[#3B82F6] dark:text-[#60A5FA] shrink-0" />
            {/* Year input */}
            <YearSelector
              value={year}
              onChange={setYear}
              min={2000}
              max={2100}
              size="compact"
              className="ml-0.5 sm:ml-1 shrink-0"
            />
            <div className="hidden sm:block h-px flex-1 min-w-[72px] rounded-full bg-gradient-to-r from-[#E5E7EB] via-[#D1D5DB] to-transparent dark:from-[#374151] dark:via-[#4B5563] dark:to-transparent ml-2 sm:ml-3" />
          </div>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7 }}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-x-4 sm:gap-x-6 gap-y-8 sm:gap-y-14 w-full"
          >
            {months.map((month, idx) => {
              const { pnl, count, percentage } = monthData[idx];
              const daysInMonth = new Date(year, idx + 1, 0).getDate();
              let color = "gray", status = "no-trades";
              if (count > 0) {
                const absPnl = Math.abs(pnl);
                // Check for BE trades using configurable threshold
                if (absPnl >= 1 && absPnl <= beThreshold) {
                  color = "blue";
                  status = "trades";
                } else if (pnl > 0) { 
                  color = "green"; 
                  status = "trades"; 
                } else if (pnl < 0) { 
                  color = "red"; 
                  status = "trades"; 
                } else { 
                  color = "gray"; 
                  status = "trades"; 
                }
              }
              return (
                <CalendarBox
                  key={month}
                  label={month}
                  subLabel={`1-${daysInMonth}`}
                  color={color}
                  pnl={pnl}
                  status={status}
                  tradeCount={count}
                  percentage={percentage}
                  isMonthView={true}
                  appearance={calendarCardAppearance}
                  onClick={() => navigate(`/month/${year}/${idx + 1}`)}
                  delay={idx * 0.03}
                />
              );
            })}
          </motion.div>
        </>
      )}
    </div>
  );
};

export default HomePage; 
